import { useState, useRef, useEffect } from "react";
import { FileText, Mail, HardDrive, Calendar, Globe, Brain, Sliders } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface MCPSettingsProps {
  mcpServices: Record<string, boolean>;
  onUpdateServices: (services: Record<string, boolean>) => void;
  isOpen: boolean;
  onClose: () => void;
  anchorEl?: HTMLElement | null;
}

const MCP_SERVICE_CONFIG = {
  notion: {
    name: "Notion",
    icon: FileText,
  },
  email: {
    name: "Email", 
    icon: Mail,
  },
  drive: {
    name: "Drive",
    icon: HardDrive,
  },
  calendar: {
    name: "Calendar",
    icon: Calendar,
  },
  websearch: {
    name: "Web Search",
    icon: Globe,
  },
  memory: {
    name: "Memory",
    icon: Brain,
  }
};

export function MCPSettings({ mcpServices, onUpdateServices, isOpen, onClose, anchorEl }: MCPSettingsProps) {
  const [localServices, setLocalServices] = useState(mcpServices);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Sync local state with props when mcpServices changes
  useEffect(() => {
    setLocalServices(mcpServices);
  }, [mcpServices]);

  const handleToggleService = (service: string) => {
    const newServices = {
      ...localServices,
      [service]: !localServices[service]
    };
    setLocalServices(newServices);
    onUpdateServices(newServices);
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        ref={popoverRef}
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -10 }}
        transition={{ duration: 0.15 }}
        className="absolute bottom-full mb-2 right-0 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl overflow-hidden"
        style={{ width: '280px' }}
      >
        {/* Header */}
        <div className="p-3 border-b border-slate-700 bg-slate-800">
          <div className="flex items-center gap-2">
            <Sliders className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-white">MCP Services</span>
          </div>
        </div>

        {/* Services List */}
        <div className="py-2">
          {Object.entries(MCP_SERVICE_CONFIG).map(([serviceKey, config]) => {
            const Icon = config.icon;
            const isEnabled = localServices[serviceKey] ?? false;
            
            return (
              <button
                key={serviceKey}
                onClick={() => handleToggleService(serviceKey)}
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-700 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-white">{config.name}</span>
                  <span className={`text-xs ${isEnabled ? 'text-green-400' : 'text-slate-500'}`}>
                    {isEnabled ? 'On' : 'Off'}
                  </span>
                </div>

                <div className="relative">
                  <div className={`w-9 h-5 rounded-full transition-colors ${
                    isEnabled ? 'bg-blue-500' : 'bg-slate-600'
                  }`}>
                    <motion.div
                      className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-md"
                      animate={{ left: isEnabled ? '18px' : '2px' }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-3 py-2 border-t border-slate-700 bg-slate-800/50">
          <button
            onClick={() => {
              const allEnabled = Object.keys(MCP_SERVICE_CONFIG).reduce((acc, key) => ({
                ...acc,
                [key]: !Object.values(localServices).every(v => v)
              }), {});
              setLocalServices(allEnabled);
              onUpdateServices(allEnabled);
            }}
            className="w-full text-xs text-slate-400 hover:text-white transition-colors"
          >
            {Object.values(localServices).every(v => v) ? 'Disable All' : 'Enable All'}
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
} 